import { FC, useMemo } from 'react';

import CollectionCard from '@/components/AppointmentScheduler/CollectionCard.tsx';
import ConditionalTooltip from '@/components/ConditionalTooltip.tsx';
import Paragraph from '@/components/text/Paragraph.tsx';

import { useEnrichedPractitionerDataForSpecificInstance } from '@/hooks/useEnrichedPractitionerData.ts';

import { AvailabilityStatus } from '@/models/appointment/Appointment.ts';

import { selectCurrentForm, useBookingStore } from '@/stores/bookingStore.ts';

import { hasOccurrenceConflict } from '@/utils/appointmentUtils.ts';

interface Props {
    onEditOccurrence: (id: string) => void;
}

const CollectionList: FC<Props> = ({ onEditOccurrence }) => {
    const {
        collection_occurrences,
        current_patient,
        collectionPastAppointments: pastAppointments,
    } = useBookingStore((state) => state);
    const current_form = useBookingStore(selectCurrentForm);
    const { data: practitioner_data } = useEnrichedPractitionerDataForSpecificInstance(
        current_patient?.instance_id ?? ''
    );

    const appointment_type_name = useMemo(() => {
        const apt_type = practitioner_data?.appointment_types.find(
            (t) => t.id === current_form?.apt_type_id_or_schedule_type_pvs_id
        );
        return apt_type?.name ?? 'Terminart';
    }, [practitioner_data, current_form?.apt_type_id_or_schedule_type_pvs_id]);

    const practitioner_name = useMemo(() => {
        const practitioner = practitioner_data?.practitioners.find((p) =>
            p.calendars.some((c) => c.pvs.id === current_form?.calendar_pvs_id)
        );
        return practitioner?.name ?? 'Behandler';
    }, [practitioner_data, current_form?.calendar_pvs_id]);

    const conflict_count = useMemo(
        () => collection_occurrences.filter((_, idx) => hasOccurrenceConflict(collection_occurrences, idx)).length,
        [collection_occurrences]
    );

    return (
        <div className="flex flex-col gap-6">
            {/* Summary header */}
            <div className="grid grid-cols-[3fr_2fr_1fr_1fr] gap-4 rounded-lg border border-slate-200 bg-slate-50 p-4">
                <div className="flex min-w-0 flex-col">
                    <span className="text-sm text-slate-500">Terminart</span>
                    <span className="truncate font-medium" title={appointment_type_name}>
                        {appointment_type_name}
                    </span>
                </div>
                <div className="flex min-w-0 flex-col">
                    <span className="text-sm text-slate-500">Behandler</span>
                    <span className="truncate font-medium" title={practitioner_name}>
                        {practitioner_name}
                    </span>
                </div>
                <div className="flex flex-col">
                    <span className="text-sm text-slate-500">Anzahl</span>
                    <span className="font-medium">
                        {pastAppointments.length + collection_occurrences.length} Termine
                    </span>
                </div>
                <div className="flex flex-col">
                    <span className="text-sm text-slate-500">Konflikte</span>
                    <span className={conflict_count > 0 ? 'font-medium text-red-600' : 'font-medium text-green-600'}>
                        {conflict_count > 0 ? `${conflict_count} Konflikte` : 'Keine'}
                    </span>
                </div>
            </div>

            {/* Appointments list */}
            <div className="flex flex-col">
                <Paragraph className="mb-2 text-sm font-medium">Alle Termine</Paragraph>

                <div className="flex max-h-[calc(75vh-200px)] flex-col overflow-y-auto overflow-x-hidden rounded-lg border border-slate-200">
                    {pastAppointments.map((app, idx) => (
                        <ConditionalTooltip
                            key={`tooltip${app.id}`}
                            label="Dieser Termin liegt in der Vergangenheit und kann nicht mehr bearbeitet werden."
                        >
                            <div>
                                <CollectionCard
                                    key={app.id}
                                    position={idx}
                                    disabled
                                    startDatetime={app.start}
                                    endDatetime={app.end}
                                    status={AvailabilityStatus.AVAILABLE}
                                    originalTime={app.start}
                                />
                            </div>
                        </ConditionalTooltip>
                    ))}
                    {collection_occurrences.map((occurrence, idx) => (
                        <CollectionCard
                            key={occurrence.id}
                            position={idx + pastAppointments.length}
                            startDatetime={occurrence.start_datetime}
                            endDatetime={occurrence.end_datetime}
                            status={
                                hasOccurrenceConflict(collection_occurrences, idx)
                                    ? AvailabilityStatus.UNAVAILABLE
                                    : occurrence.availability
                            }
                            originalTime={occurrence.original_time}
                            onEdit={() => onEditOccurrence(occurrence.id)}
                        />
                    ))}
                </div>
            </div>
        </div>
    );
};

export default CollectionList;
